/**
 * @fileoverview Provide shared table interaction over rows already supplied by a workspace.
 *
 * `AppColumn` children declare presentation/editing rules; they are consumed by the table rather than rendered independently. Sorting and paging operate on the supplied array, so backend query bounds and global counts remain caller responsibilities. Row edits and reordering are delegated to callbacks; the table does not persist domain changes.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import AppIcon from "./AppIcon";

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.875rem'
};

const thStyle = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontWeight: 600,
  color: 'var(--text-color)',
  backgroundColor: 'var(--surface-50, var(--surface-ground))',
  borderBottom: '2px solid var(--surface-border)',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap'
};

const tdStyle = {
  padding: '0.75rem 1rem',
  color: 'var(--text-color)',
  borderBottom: '1px solid var(--surface-border)'
};



function AppDataTable({
  value,
  children,
  size,
  stripedRows,
  emptyMessage,
  responsiveLayout,
  sortField: defaultSortField,
  sortOrder: defaultSortOrder,
  paginator,
  rows: rowsPerPage = 25,
  rowsPerPageOptions,
  onRowClick,
  selectionMode,
  loading,
  showGridlines,
  editMode,
  reorderableRows,
  onRowReorder,
  className,
  tableStyle: tableStyleProp,
  wrapperStyle: wrapperStyleProp
}) {
  const [sortField, setSortField] = useState(defaultSortField || null);
  const [sortOrder, setSortOrder] = useState(defaultSortOrder || 1);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowsPerPage);
  const [editingCell, setEditingCell] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const tableRef = useRef(null);

  const columns = React.Children.toArray(children).filter(Boolean).map((child) => ({
    field: child.props.field,
    header: child.props.header,
    body: child.props.body,
    sortable: child.props.sortable,
    sortField: child.props.sortField,
    style: child.props.style,
    editor: child.props.editor,
    editable: child.props.editable,
    onCellEditComplete: child.props.onCellEditComplete,
    rowReorder: child.props.rowReorder
  }));

  const sorted = useMemo(

  () => {
    if (!value) return [];
    const actualSortField = sortField ?
    columns.find((c) => c.field === sortField)?.sortField || sortField :
    null;
    if (!actualSortField) return [...value];
    return [...value].sort(

    (a, b) => {
      const aVal = a[actualSortField];
      const bVal = b[actualSortField];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * sortOrder;
      }
      return String(aVal).localeCompare(String(bVal)) * sortOrder;
    });
  }, [value, sortField, sortOrder]);

  const totalRecords = sorted.length;
  const paginatedData = paginator ?
  sorted.slice(currentPage * pageSize, (currentPage + 1) * pageSize) :
  sorted;

  // Reset page when data changes
  useEffect(() => {
    if (paginator && currentPage > 0 && currentPage * pageSize >= totalRecords) {
      setCurrentPage(0);
    }
  }, [totalRecords, paginator, currentPage, pageSize]);

  const handleSort =

  (col) => {
    if (!col.sortable) return;
    const field = col.field;
    if (sortField === field) {
      setSortOrder((prev) => prev * -1);
    } else {
      setSortField(field);
      setSortOrder(1);
    }
  };

  const handleCellClick =

  (rowIndex, colIndex, col, rowData) => {
    if (editMode !== 'cell' || !col.editor) return;
    if (col.editable && !col.editable(rowData)) return;
    const globalRowIndex = paginator ? currentPage * pageSize + rowIndex : rowIndex;
    setEditingCell({ rowIndex: globalRowIndex, colIndex });
  };

  // Click outside to exit edit mode
  useEffect(

  () => {
    if (!editingCell) return;
    const handle =

    (e) => {
      if (tableRef.current && !tableRef.current.contains(e.target)) {
        setEditingCell(null);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [editingCell]);

  // Escape to exit edit mode
  useEffect(

  () => {
    if (!editingCell) return;
    const handle =

    (e) => {
      if (e.key === 'Escape') setEditingCell(null);
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [editingCell]);

  const handleDragStart =

  (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver =

  (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop =

  (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex || !onRowReorder) return;
    const reordered = [...(value || [])];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    onRowReorder({ value: reordered });
    setDragIndex(null);
  };

  const isSmall = size === 'small';
  const cellPadding = isSmall ? '0.5rem 0.75rem' : undefined;
  const gridBorder = showGridlines ? '1px solid var(--surface-border)' : undefined;

  const wrapperStyle = {
    ...(responsiveLayout ? { overflowX: 'auto', WebkitOverflowScrolling: 'touch' } : {}),
    ...wrapperStyleProp
  };

  const totalPages = paginator ? Math.max(1, Math.ceil(totalRecords / pageSize)) : 0;
  const rangeStart = totalRecords === 0 ? 0 : currentPage * pageSize + 1;
  const rangeEnd = Math.min((currentPage + 1) * pageSize, totalRecords);

  return (
    <div className={className || ''} style={{ position: 'relative' }}>
            {loading &&
      <div style={{
        position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)',
        zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
                    <div className="app-spinner" role="status" aria-label="Loading" />
                </div>}

            <div style={wrapperStyle}>
                <table style={{ ...tableStyle, ...tableStyleProp }} ref={tableRef}>
                    <thead>
                        <tr>
                            {columns.map((col, i) =>
              <th
                key={i}
                style={{
                  ...thStyle,
                  ...(cellPadding ? { padding: cellPadding } : {}),
                  cursor: col.sortable ? 'pointer' : 'default',
                  ...(gridBorder ? { border: gridBorder } : {}),
                  ...col.style
                }}
                onClick={() => handleSort(col)}>

                                    {col.header}
                                    {col.sortable && sortField === col.field && <AppIcon icon={

                `pi ${sortOrder === 1 ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down'}`}
                style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }} />}


                                </th>
              )}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length === 0 && emptyMessage ?
            <tr>
                                <td colSpan={columns.length} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-color-secondary)' }}>
                                    {emptyMessage}
                                </td>
                            </tr> :

            paginatedData.map((row, rowIndex) => {
              const globalRowIndex = paginator ? currentPage * pageSize + rowIndex : rowIndex;
              return (
                <tr
                  key={rowIndex}
                  style={{
                    backgroundColor: stripedRows && rowIndex % 2 === 1 ?
                    'var(--surface-50, var(--surface-ground))' :
                    'transparent',
                    cursor: selectionMode || onRowClick ? 'pointer' : undefined
                  }}
                  onClick={onRowClick ? () => onRowClick({ data: row, index: globalRowIndex }) : undefined}
                  draggable={reorderableRows ? true : undefined}
                  onDragStart={reorderableRows ? (e) => handleDragStart(e, globalRowIndex) : undefined}
                  onDragOver={reorderableRows ? handleDragOver : undefined}
                  onDrop={reorderableRows ? (e) => handleDrop(e, globalRowIndex) : undefined}>

                                        {columns.map(

                                        (col, colIndex) => {
                    const isEditing = editingCell &&
                    editingCell.rowIndex === globalRowIndex &&
                    editingCell.colIndex === colIndex;

                    let cellContent;
                    if (col.rowReorder) {
                      cellContent = <AppIcon icon="pi pi-bars"
                      style={{ cursor: 'grab', color: 'var(--text-color-secondary)' }} />;

                    } else if (isEditing && col.editor) {
                      cellContent = col.editor({
                        value: row[col.field],
                        field: col.field,
                        rowData: row,
                        rowIndex: globalRowIndex,
                        editorCallback: (newValue) => {
                          if (col.onCellEditComplete) {
                            col.onCellEditComplete({
                              rowData: row,
                              newValue,
                              field: col.field,
                              rowIndex: globalRowIndex
                            });
                          }
                          setEditingCell(null);
                        }
                      });
                    } else if (col.body) {
                      cellContent = col.body(row, { rowIndex: globalRowIndex, field: col.field });
                    } else {
                      cellContent = row[col.field];
                    }

                    return (
                      <td
                        key={colIndex}
                        style={{
                          ...tdStyle,
                          ...(cellPadding ? { padding: cellPadding } : {}),
                          ...(gridBorder ? { border: gridBorder } : {}),
                          ...col.style
                        }}
                        onClick={(e) => {
                          if (editMode === 'cell' && col.editor) {
                            e.stopPropagation();
                            handleCellClick(rowIndex, colIndex, col, row);
                          }
                        }}>

                                                    {cellContent}
                                                </td>);

                  })}
                                    </tr>);

            })}

                    </tbody>
                </table>
            </div>

            {paginator && totalRecords > 0 &&
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.75rem 1rem', borderTop: '1px solid var(--surface-border)',
        fontSize: '0.875rem', color: 'var(--text-color-secondary)'
      }}>
                    <span>{rangeStart}–{rangeEnd} of {totalRecords}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {rowsPerPageOptions &&
          <select
            value={pageSize}
            onChange={(e) => {setPageSize(Number(e.target.value));setCurrentPage(0);}}
            style={{
              padding: '0.25rem 0.5rem', borderRadius: '4px',
              border: '1px solid var(--surface-border)',
              backgroundColor: 'var(--surface-section)', color: 'var(--text-color)',
              fontSize: '0.875rem'
            }}>

                                {rowsPerPageOptions.map((opt) =>
            <option key={opt} value={opt}>{opt}</option>
            )}
                            </select>}

                        <button
            type="button"
            disabled={currentPage === 0}
            onClick={() => setCurrentPage((p) => p - 1)}
            style={{
              border: '1px solid var(--surface-border)', borderRadius: '4px',
              backgroundColor: 'var(--surface-section)', color: 'var(--text-color)',
              padding: '0.25rem 0.5rem', cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
              opacity: currentPage === 0 ? 0.5 : 1
            }}>

                            <AppIcon icon="pi pi-chevron-left" style={{ fontSize: '0.75rem' }} />
                        </button>
                        <span>Page {currentPage + 1} of {totalPages}</span>
                        <button
            type="button"
            disabled={currentPage >= totalPages - 1}
            onClick={() => setCurrentPage((p) => p + 1)}
            style={{
              border: '1px solid var(--surface-border)', borderRadius: '4px',
              backgroundColor: 'var(--surface-section)', color: 'var(--text-color)',
              padding: '0.25rem 0.5rem', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
              opacity: currentPage >= totalPages - 1 ? 0.5 : 1
            }}>

                            <AppIcon icon="pi pi-chevron-right" style={{ fontSize: '0.75rem' }} />
                        </button>
                    </div>
                </div>}

        </div>);

}

/**
 * Declarative column configuration consumed by AppDataTable. It intentionally renders no independent content; placing it outside the table does not create a column.
 */
function AppColumn() {
  return null; // Declarative only; consumed by AppDataTable
}

export { AppDataTable, AppColumn };
